import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Plus, Search, Ticket } from 'lucide-react';

import { request } from '@/api/client';
import type { Page, PromoCode, PromoCodeStatus } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Modal,
  Select,
  TableSkeleton,
  TextInput,
} from '@/components/ui';
import { dateTime } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';

const STATUS_TONE: Record<PromoCodeStatus, 'neutral' | 'positive' | 'critical'> = {
  open: 'positive',
  redeemed: 'neutral',
  revoked: 'critical',
};

export function VipCodesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PromoCodeStatus | ''>('');
  const [page, setPage] = useState(1);
  const [minting, setMinting] = useState(false);
  const [revoking, setRevoking] = useState<PromoCode | null>(null);
  const debouncedSearch = useDebounced(search);
  const language = i18n.language;
  const [pageSize, setPageSize] = usePageSize('vip-codes');

  const { data, error, loading, reload } = useResource<Page<PromoCode>>(
    '/api/v1/admin/promo-codes',
    {
      search: debouncedSearch,
      status: status || undefined,
      page,
      page_size: pageSize,
    },
    [debouncedSearch, status, page, pageSize],
  );

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('vip.copied'));
    } catch {
      // Clipboard is refused outside a secure context; selecting the text by
      // hand still works, so this is not worth an error.
    }
  }

  async function revoke(code: PromoCode) {
    try {
      await request(`/api/v1/admin/promo-codes/${code.id}/revoke`, { method: 'POST' });
      toast.success(t('vip.revoked'));
      setRevoking(null);
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  const columns: Column<PromoCode>[] = [
    {
      key: 'code',
      header: t('vip.code'),
      render: (row) => (
        <div className="flex items-center gap-2">
          <code className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-xs tracking-wider text-ink">
            {row.code}
          </code>
          <IconButton
            variant="ghost"
            label={t('vip.copy')}
            icon={<Copy className="h-3.5 w-3.5" />}
            onClick={() => copy(row.code)}
          />
        </div>
      ),
    },
    {
      key: 'label',
      header: t('vip.label'),
      render: (row) => (
        <span className="text-ink-muted">
          {row.label ?? <span aria-hidden className="text-ink-subtle">—</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('vip.status'),
      render: (row) => (
        <Badge tone={STATUS_TONE[row.status]}>{t(`vip.statuses.${row.status}`)}</Badge>
      ),
    },
    {
      key: 'redeemedBy',
      header: t('vip.redeemedBy'),
      render: (row) =>
        row.redeemed_by_name ? (
          <div className="min-w-0">
            <div className="truncate text-ink">{row.redeemed_by_name}</div>
            <div className="truncate text-2xs text-ink-subtle">{row.redeemed_by_email}</div>
          </div>
        ) : (
          <span aria-hidden className="text-ink-subtle">
            —
          </span>
        ),
    },
    {
      key: 'redeemedAt',
      header: t('vip.redeemedAt'),
      align: 'right',
      render: (row) => (
        <span className="text-ink-subtle">
          {dateTime(row.redeemed_at, language) ?? <span aria-hidden>—</span>}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('vip.revoke'),
      align: 'right',
      render: (row) =>
        row.status === 'open' ? (
          <Button className="text-xs" onClick={() => setRevoking(row)}>
            {t('vip.revoke')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('vip.title')}
        subtitle={t('vip.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setMinting(true)}
          >
            {t('vip.create')}
          </Button>
        }
      />

      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              aria-label={t('common.search')}
              placeholder={`${t('vip.code')} / ${t('vip.label')}`}
              className="pl-9"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            aria-label={t('vip.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as PromoCodeStatus | '');
              setPage(1);
            }}
          >
            <option value="">{t('common.all')}</option>
            {(['open', 'redeemed', 'revoked'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`vip.statuses.${value}`)}
              </option>
            ))}
          </Select>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={6} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Ticket className="h-8 w-8" />}
            title={t('vip.emptyTitle')}
            body={t('vip.emptyBody')}
            action={
              <Button variant="primary" onClick={() => setMinting(true)}>
                {t('vip.create')}
              </Button>
            }
          />
        ) : (
          <>
            <DataTable
              caption={t('vip.title')}
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(row) => row.id}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </div>

      {minting ? (
        <MintCodes
          onClose={() => setMinting(false)}
          onCreated={(created) => {
            setMinting(false);
            toast.success(t('vip.created', { count: created }));
            reload();
          }}
        />
      ) : null}

      {revoking ? (
        <Modal
          open
          onClose={() => setRevoking(null)}
          title={t('vip.revokeTitle')}
          footer={
            <>
              <Button onClick={() => setRevoking(null)}>{t('common.cancel')}</Button>
              <Button variant="danger" onClick={() => revoke(revoking)}>
                {t('vip.revoke')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-muted">
            {t('vip.revokeBody', { code: revoking.code })}
          </p>
        </Modal>
      ) : null}
    </>
  );
}

function MintCodes({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (count: number) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [count, setCount] = useState('1');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const parsed = Number(count);
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 200;

  async function create() {
    setBusy(true);
    try {
      const created = await request<PromoCode[]>('/api/v1/admin/promo-codes', {
        method: 'POST',
        body: { count: parsed, label: label || null },
      });
      onCreated(created.length);
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('vip.create')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={create}>
            {busy ? t('vip.creating') : t('vip.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('vip.count')} hint={t('vip.countHelp')}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={count}
              invalid={!valid}
              onChange={(event) => setCount(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('vip.label')} hint={t('vip.labelHelp')}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              maxLength={200}
              placeholder={t('vip.labelPlaceholder')}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}
